import { X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'

import { cx } from './cx'

/*
 | Diálogo modal sobre el `<dialog>` nativo.
 |
 | Se usa el elemento del navegador y no un `div` con `position: fixed` porque
 | trae gratis las cuatro cosas que casi nadie implementa bien a mano: la trampa
 | de foco, el cierre con Escape, el fondo inerte —el resto de la página deja de
 | recibir clics y deja de ser navegable con el tabulador— y la capa superior,
 | que no depende de ningún `z-index`.
 |
 | En móvil se pega abajo, que es donde llega el pulgar; en escritorio va
 | centrado. La misma pieza sirve para los dos casos sin duplicar componente.
 */

interface Props {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  /** Botones del pie. Van en el orden en que se leen: cancelar, confirmar. */
  footer?: ReactNode
  className?: string
}

export function Dialog({ open, onClose, title, description, children, footer, className }: Props) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (dialog === null) return

    // `showModal()` y no el atributo `open`: sólo el método activa el modo
    // modal de verdad, con foco atrapado y fondo inerte.
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    const dialog = ref.current
    if (dialog === null) return

    // El navegador cierra por Escape sin avisar a React, así que hay que
    // escuchar `close` o el estado se queda desincronizado y el diálogo no
    // vuelve a abrirse.
    const onNativeClose = (): void => onClose()
    dialog.addEventListener('close', onNativeClose)

    return () => dialog.removeEventListener('close', onNativeClose)
  }, [onClose])

  return (
    <dialog
      ref={ref}
      aria-labelledby="dialog-title"
      // El diálogo nativo trae márgenes y borde propios; se anulan y se
      // posiciona a mano para poder pegarlo abajo en móvil.
      className={cx(
        'm-0 max-h-[92dvh] w-full max-w-none bg-transparent p-0 backdrop:bg-black/50',
        'fixed inset-x-0 bottom-0 top-auto',
        'sm:inset-0 sm:m-auto sm:h-fit sm:max-w-lg',
      )}
      onClick={(event) => {
        // Clic en el fondo cierra. Se compara el objetivo con el propio
        // `<dialog>`: su caja ocupa toda la pantalla, así que un clic «fuera»
        // llega marcado como suyo y no como del contenido.
        if (event.target === ref.current) onClose()
      }}
    >
      <div
        className={cx(
          'flex max-h-[92dvh] flex-col rounded-t-panel border border-line bg-surface-raised shadow-float',
          'pb-safe sm:rounded-panel',
          className,
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line p-5">
          <div className="min-w-0">
            <h2 id="dialog-title" className="text-heading font-semibold text-ink">
              {title}
            </h2>
            {description !== undefined && (
              <p className="mt-1 text-body text-ink-muted">{description}</p>
            )}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 grid h-9 w-9 shrink-0 place-items-center rounded-control text-ink-subtle transition-colors hover:bg-surface-sunken hover:text-ink"
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </header>

        {/* El cuerpo es lo único que hace scroll: la cabecera y el pie se
            quedan a la vista, y en móvil el botón de confirmar no se pierde
            debajo de un formulario largo. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>

        {footer !== undefined && (
          <footer className="flex flex-wrap justify-end gap-2 border-t border-line p-4">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  )
}
